import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";

// Pages
import LoginPage from "./pages/LoginPage";
import SetupPage from "./pages/SetupPage";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import Dashboard from "./pages/Dashboard";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetail from "./pages/ProjectDetail";
import KanbanBoard from "./pages/KanbanBoard";
// CalendarPage removido - não usado no sidebar
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import DailyControlPage from "./pages/DailyControlPage";
import ReportsPage from "./pages/ReportsPage";
import PaymentsPage from "./pages/PaymentsPage";
import TasksPage from "./pages/TasksPage";
import FeedbacksPage from "./pages/FeedbacksPage";
import HumanasPage from "./pages/HumanasPage";
import HelpPage from "./pages/HelpPage";
// SectorTimelinePage removido - Visão Anual não utilizada
import PendenciasPage from "./pages/PendenciasPage";
// TermosRankingPage removido - não utilizado
import PerformancePage from "./pages/PerformancePage";
import RelatorioPessoaPage from "./pages/RelatorioPessoaPage";
import FolhaCltPage from "./pages/FolhaCltPage";
import FolhaPagamentoPage from "./pages/FolhaPagamentoPage";
import FeriadosPage from "./pages/FeriadosPage";
import CompetenciasPage from "./pages/CompetenciasPage";
// OsEscalaPage removido - funcionalidade unificada em OsGestaoPage
import OsGestaoPage from "./pages/OsGestaoPage";
import EscalaDiaPage from "./pages/EscalaDiaPage";
import EstoquePage from "./pages/EstoquePage";
import BancoIdeiasPage from "./pages/BancoIdeiasPage";
import CicloMelhoriaPage from "./pages/CicloMelhoriaPage";

// Layout
import MainLayout from "./components/layout/MainLayout";
import OrcamentosPage from "./pages/OrcamentosPage";

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole === "super_admin" && user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredRole === "admin" && !["admin", "super_admin"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredRole === "admin_ou_comercial" && !["admin", "super_admin", "diretoria"].includes(user.role) && !(user.permissions || []).includes("comercial")) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Redirect based on role
const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Sempre vai para dashboard da empresa - super admin acessa via /admin-secret
  return <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup-system" element={<SetupPage />} />

      {/* Root redirect */}
      <Route path="/" element={<RoleBasedRedirect />} />

      {/* Super Admin Routes - Hidden path */}
      <Route
        path="/admin-secret"
        element={
          <ProtectedRoute requiredRole="super_admin">
            <MainLayout title="Painel Administrativo" />
          </ProtectedRoute>
        }
      >
        <Route index element={<SuperAdminDashboard />} />
        <Route path="companies" element={<SuperAdminDashboard />} />
        <Route path="feedbacks" element={<FeedbacksPage />} />
      </Route>

      {/* Company Admin/User Routes */}
      <Route
        path="/banco-ideias"
        element={
          <ProtectedRoute>
            <MainLayout title="Banco de Ideias" />
          </ProtectedRoute>
        }
      >
        <Route index element={<BancoIdeiasPage />} />
      </Route>

      <Route
        path="/orcamentos"
        element={
          <ProtectedRoute>
            <MainLayout title="Orçamentos" />
          </ProtectedRoute>
        }
      >
        <Route index element={<OrcamentosPage />} />
      </Route>

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout title="Dashboard" />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
      </Route>

      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <MainLayout title="Projetos" />
          </ProtectedRoute>
        }
      >
        <Route index element={<ProjectsPage />} />
      </Route>

      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <MainLayout title="Projeto" />
          </ProtectedRoute>
        }
      >
        <Route index element={<ProjectDetail />} />
      </Route>

      <Route
        path="/projects/:projectId/kanban"
        element={
          <ProtectedRoute>
            <MainLayout title="Kanban" />
          </ProtectedRoute>
        }
      >
        <Route index element={<KanbanBoard />} />
      </Route>

      {/* Rota Pendências removida */}
      {/* Rota Visão Anual removida */}

      <Route
        path="/daily-control"
        element={
          <ProtectedRoute>
            <MainLayout title="Escala do Dia" />
          </ProtectedRoute>
        }
      >
        <Route index element={<DailyControlPage />} />
      </Route>

      {/* Rota Relatórios removida */}

      {/* TermosRankingPage removido - rota desativada */}

      <Route
        path="/relatorio-pessoa"
        element={
          <ProtectedRoute>
            <MainLayout title="Relatório por Pessoa" />
          </ProtectedRoute>
        }
      >
        <Route index element={<RelatorioPessoaPage />} />
      </Route>

      <Route
        path="/folha-clt"
        element={
          <ProtectedRoute>
            <MainLayout title="Ponto CLT com Obs." />
          </ProtectedRoute>
        }
      >
        <Route index element={<FolhaCltPage />} />
      </Route>

      <Route
        path="/folha-pagamento"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout title="Folha de Pagamento CLT" />
          </ProtectedRoute>
        }
      >
        <Route index element={<FolhaPagamentoPage />} />
      </Route>

      {/* OsEscalaPage removido - funcionalidade unificada em OsGestaoPage */}

      <Route
        path="/os-gestao"
        element={
          <ProtectedRoute>
            <MainLayout title="Relação de OSs" />
          </ProtectedRoute>
        }
      >
        <Route index element={<OsGestaoPage />} />
      </Route>

      {/* Rota Relatório Operacional removida */}

      <Route
        path="/estoque"
        element={
          <ProtectedRoute>
            <MainLayout title="Controle de Estoque" />
          </ProtectedRoute>
        }
      >
        <Route index element={<EstoquePage />} />
      </Route>

      <Route
        path="/feriados"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout title="Gestão de Feriados" />
          </ProtectedRoute>
        }
      >
        <Route index element={<FeriadosPage />} />
      </Route>

      <Route
        path="/competencias"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout title="Fechamento de Competência" />
          </ProtectedRoute>
        }
      >
        <Route index element={<CompetenciasPage />} />
      </Route>

      <Route
        path="/ciclo-melhoria"
        element={
          <ProtectedRoute>
            <MainLayout title="Ciclo de Melhoria" />
          </ProtectedRoute>
        }
      >
        <Route index element={<CicloMelhoriaPage />} />
      </Route>

      <Route
        path="/performance"
        element={
          <ProtectedRoute>
            <MainLayout title="Relatório de Performance" />
          </ProtectedRoute>
        }
      >
        <Route index element={<PerformancePage />} />
      </Route>

      {/* Rota Pagamentos removida */}

      <Route
        path="/humanas"
        element={
          <ProtectedRoute>
            <MainLayout title="Humanas" />
          </ProtectedRoute>
        }
      >
        <Route index element={<HumanasPage />} />
      </Route>

      {/* CalendarPage removido - não utilizado */}

      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <MainLayout title="Tarefas" />
          </ProtectedRoute>
        }
      >
        <Route index element={<TasksPage />} />
      </Route>

      <Route
        path="/users"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout title="Usuários" />
          </ProtectedRoute>
        }
      >
        <Route index element={<UsersPage />} />
      </Route>

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <MainLayout title="Configurações" />
          </ProtectedRoute>
        }
      >
        <Route index element={<SettingsPage />} />
      </Route>

      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <MainLayout title="Ajuda" />
          </ProtectedRoute>
        }
      >
        <Route index element={<HelpPage />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
